import { styled } from '@compiled/react';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { WrappedComponentProps } from 'react-intl-next';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/custom-theme-button';
import EditIcon from '@atlaskit/icon/core/edit';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import {
	EMBEDDED_CONFLUENCE_METRICS_PREFIX,
	EMBEDDED_EDIT_CLICK,
	useEmbeddedConfluenceEventMarkers,
} from '@atlassian/embedded-confluence';
import { getIframeObservable_DO_NOT_USE } from '@atlassian/embedded-confluence/getIframeObservable';

import { ExperienceActionMeasures } from '@confluence/action-measures/entry-points/ExperienceActionMeasures.ts';
import {
	EDIT_INTENT_EDIT_FMP_ACTION,
	EDIT_INTENT_EDIT_TTI_ACTION,
	EDIT_ROUTE_CHANGE_ACTION,
	VIEW_EDIT_BUTTON_CLICKED_ACTION,
} from '@confluence/browser-metrics/entry-points/metric-keys.ts';
import { rememberScrolledPosition } from '@confluence/editor-scroll-position/entry-points/rememberScrolledPosition.ts';
import { VIEW_PAGE_EDIT_BUTTON_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { EditPopupEligibleMigration } from '@confluence/fabric-page-optin-migration/entry-points/EditPopupEligibleMigration.ts';
import { UNSAFE_noExposureExp, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import {
	BLOG_EDIT_BUTTON_CLICK,
	BLOG_EDIT_SHORTCUT_E,
	PAGE_EDIT_BUTTON_CLICK,
	PAGE_EDIT_SHORTCUT_E,
	SPACE_EDIT_BUTTON_CLICK,
	SPACE_EDIT_SHORTCUT_E,
} from '@confluence/edit-common/entry-points/event-names.ts';
import { useEditSourceActions } from '@confluence/edit-common/entry-points/useEditSource.ts';
import {
	COMPANY_HUB_EDIT,
	EDIT_BLOG,
	EDIT_BLOG_V2,
	EDIT_PAGE,
	EDIT_PAGE_EMBED,
	EDIT_PAGE_V2,
	SPACE_OVERVIEW,
} from '@confluence/named-routes/entry-points/routes.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import type { Route } from '@confluence/route/entry-points/Route.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { getSpaTransitionDetails } from '@confluence/route-manager/entry-points/GlobalAnchorHandler.ts';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { EDIT_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';

import {
	EditContentButtonPreloader,
	resolvePendingPrefetchAnalytics,
	type SendPrefetchAnalyticsEvent,
} from './EditContentButtonPreloader.tsx';
import { useFabricEligibilityMigrationQuery } from './useFabricEligibilityMigrationQuery.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		minWidth: '30px',
	},
});

const i18n = defineMessages({
	editLabel: {
		id: 'edit-button.edit.label',
		defaultMessage: 'Edit this content',
		description: 'aria-label for the edit button',
	},
	editEmbedLabel: {
		id: 'edit-button.edit.embed.label',
		defaultMessage: 'Edit this embed',
		description: 'aria-label for the edit button for an embedded link',
	},
	editText: {
		id: 'edit-button.edit.text',
		defaultMessage: 'Edit',
		description: 'edit text for edit button',
	},
});

type EditContentButtonComponentProps = {
	contentId: string;
	contentType?: string;
	isEmbeddedContent?: boolean;
	isFabricSupported?: boolean;
	isCompanyHubButton?: boolean;
	onRender?: () => null;
	spaceKey?: string;
	migrateEPData?: (onSuccess?: (data?: any) => void, onFailure?: (err?: any) => void) => void;
};

const noop = () => null;

const EditContentButtonComponentInner: React.FC<
	EditContentButtonComponentProps & WrappedComponentProps
> = memo(
	({
		contentId,
		contentType,
		intl,
		isEmbeddedContent = false,
		isFabricSupported,
		onRender = noop,
		spaceKey,
		migrateEPData,
		isCompanyHubButton = false,
	}) => {
		const editButtonRef = useRef<HTMLElement>(null);
		const [localEditSource, setLocalEditSource] = useState('');
		const [isPopupVisible, setIsPopupVisible] = useState(false); // State to control popup visibility
		const { loadEditor } = useEditPageLoadingActions();
		const { setEditSource } = useEditSourceActions();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const sendPrefetchAnalyticsEvent = useCallback<SendPrefetchAnalyticsEvent>(
			(action, analyticsContentId, attributes) => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action,
						actionSubject: 'editButtonCollabDraftPrefetch',
						actionSubjectId: analyticsContentId,
						source: 'editButton',
						attributes,
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		const routesContext = useContext(RoutesContext);
		const [{ searchSessionId, additionalAnalytics }] = useSearchSessionId();
		const { match } = routesContext;
		const { routePolicy } = useNavigationPolicy();
		const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
			source: 'page-tree-quick-action-edit',
		});
		const { fabricEditorEligibility, isFabricEligibilityLoading } =
			useFabricEligibilityMigrationQuery({ contentId, isFabricSupported });

		const editShortcutStrings = useShortcutStrings({ shortcut: EDIT_SHORTCUT });

		const currentRouteName = match?.name;
		const isBlogpost = contentType === 'blogpost';
		const linkParams = useMemo<{
			contentId: string;
			spaceKey: string | undefined;
			contentType: string;
		}>(
			() => ({
				contentId,
				spaceKey,
				contentType: isBlogpost ? 'blog' : 'pages',
			}),
			[contentId, spaceKey, isBlogpost],
		);
		const dataTestId = isFabricSupported ? 'fabric-edit-button' : undefined;

		const sendMetrics = useEmbeddedConfluenceEventMarkers();

		let routeType: Route;

		// Currently a temporary check for blogs is added here until Embedded Pages supports blogs.
		if (isEmbeddedContent && !isBlogpost) {
			routeType = EDIT_PAGE_EMBED;
		} else if (isCompanyHubButton) {
			routeType = COMPANY_HUB_EDIT;
		} else if (isFabricSupported) {
			routeType = isBlogpost ? EDIT_BLOG_V2 : EDIT_PAGE_V2;
		} else {
			routeType = isBlogpost ? EDIT_BLOG : EDIT_PAGE;
		}

		useEffect(() => {
			let entryPoint = PAGE_EDIT_BUTTON_CLICK;

			if (isBlogpost) {
				entryPoint = BLOG_EDIT_BUTTON_CLICK;
			}

			const isSpaceOverview = currentRouteName === SPACE_OVERVIEW.name;

			if (isSpaceOverview) {
				entryPoint = SPACE_EDIT_BUTTON_CLICK;
			}

			setEditSource(entryPoint);
			setLocalEditSource(entryPoint);
		}, [isBlogpost, setEditSource, currentRouteName, setLocalEditSource]);

		const clickEditPencilButton = useCallback(
			() => editButtonRef && (ReactDOM.findDOMNode(editButtonRef.current) as HTMLElement)?.click(),
			[],
		);

		const measureEditButtonClickConversion = useCallback(
			(isClick: boolean) => {
				if (!isClick) {
					//shortcut
					let entryPoint = PAGE_EDIT_SHORTCUT_E;

					if (isBlogpost) {
						entryPoint = BLOG_EDIT_SHORTCUT_E;
					}

					const isSpaceOverview = currentRouteName === SPACE_OVERVIEW.name;

					if (isSpaceOverview) {
						entryPoint = SPACE_EDIT_SHORTCUT_E;
					}

					setEditSource(entryPoint);
					setLocalEditSource(entryPoint);
				}

				ExperienceActionMeasures.markMeasureStart(EDIT_INTENT_EDIT_FMP_ACTION);
				ExperienceActionMeasures.markMeasureStart(EDIT_INTENT_EDIT_TTI_ACTION);

				ExperienceActionMeasures.markMeasureEnd(VIEW_EDIT_BUTTON_CLICKED_ACTION);

				ExperienceActionMeasures.markMeasureStart(EDIT_ROUTE_CHANGE_ACTION);
			},
			[isBlogpost, currentRouteName, setEditSource, setLocalEditSource],
		);

		const onClickEdit = useCallback(
			(e: any) => {
				measureEditButtonClickConversion(e.isTrusted);
				rememberScrolledPosition(contentId);

				const details = getSpaTransitionDetails(e, routesContext);
				const routePolicyPushInNewTab =
					routePolicy &&
					!routePolicy.willPushInSelf(currentRouteName, contentId, routeType.name, contentId);

				if (!isBlogpost && Boolean(migrateEPData) && routePolicyPushInNewTab) {
					e.preventDefault();
					const navigateToEmbeddedEditor = () => {
						routesContext.push(EDIT_PAGE_EMBED.toUrl(linkParams));
					};
					const navigateToClassicEditor = () => {
						routesContext.push(routeType.toUrl(linkParams));
					};
					migrateEPData!(navigateToEmbeddedEditor, navigateToClassicEditor);
				}

				/**
				 * If we end up opening editor route in a new tab (regardless route policy specified it or user does cmd+click etc.),
				 * we don't need to show the editor loading component in the current tab and preload editor data.
				 * The opened new tab will load the editor data with the loading icon
				 * In this case, we fallback to `<Link>`'s `push` method by RouteManager
				 */
				if (details?.href && !routePolicyPushInNewTab) {
					e.preventDefault();
					e.stopPropagation();

					if (!e.isTrusted) {
						// Preload the editor route when user used the "e" shortcut, as otherwise
						// the standard preloading logic isn't triggered
						routesContext.preloadRoute(details.href);
					}

					loadEditor({
						contentId,
						spaceKey,
						redirectUrl: details.href,
						contentType,
						isFabricSupported,
						isEmbeddedContent,
					});
				}
			},
			[
				measureEditButtonClickConversion,
				contentId,
				routesContext,
				routePolicy,
				currentRouteName,
				routeType,
				isBlogpost,
				migrateEPData,
				linkParams,
				loadEditor,
				spaceKey,
				contentType,
				isFabricSupported,
				isEmbeddedContent,
			],
		);

		const isOnEditRoute = useIsEditorPage();

		const mode = match?.query?.mode;
		const fireClickAnalytics = useCallback(
			(e: any, analyticsEvent: any) => {
				analyticsEvent
					.update({
						data: {
							action: 'clicked',
							actionSubject: 'editButton',
							attributes: {
								componentVersion: 'v2',
								contentType,
								contentId,
								isFabricSupported,
								navdexPointType: END,
								searchSessionId,
								...additionalAnalytics,
								mode,
								isOnEditRoute,
							},
							source: e.isTrusted ? 'editContentButton' : 'editShortcut',
						},
						type: 'sendTrackEvent',
					})
					.fire();
			},
			[
				contentType,
				contentId,
				isFabricSupported,
				searchSessionId,
				mode,
				isOnEditRoute,
				additionalAnalytics,
			],
		);

		/**
		 * Cleanup with the platform_editor_on_edit_convert experiment.
		 */
		const [convertOnEditExpConfig] = UNSAFE_noExposureExp('platform_editor_on_edit_convert');
		const showConvertPagePopup =
			!isFabricSupported &&
			typeof fabricEditorEligibility === 'string' &&
			['SUPPORTED', 'SUPPORTED_WITH_MIGRATION'].includes(fabricEditorEligibility);

		const handleButtonClick = useCallback(
			(e: React.MouseEvent, analyticsEvent: any) => {
				resolvePendingPrefetchAnalytics(contentId, sendPrefetchAnalyticsEvent);

				if (
					showConvertPagePopup &&
					expValEquals('platform_editor_on_edit_convert', 'isEnabled', true)
				) {
					e.preventDefault(); // Prevent default action
					setIsPopupVisible((prev) => !prev); // Toggle popup visibility
					fireClickAnalytics(e, analyticsEvent);
				} else {
					if (isEmbeddedContent) {
						const observableObject = getIframeObservable_DO_NOT_USE();
						const uniqueKey = observableObject?.object?.uniqueKey;
						sendMetrics(
							{
								timestamp: Date.now(),
								...(uniqueKey ? { customData: { uniqueKey } } : {}),
							},
							`${EMBEDDED_CONFLUENCE_METRICS_PREFIX}${EMBEDDED_EDIT_CLICK}`,
						);
					}
					fireClickAnalytics(e, analyticsEvent);
					onClickEdit(e);
				}
			},
			[
				contentId,
				sendPrefetchAnalyticsEvent,
				showConvertPagePopup,
				fireClickAnalytics,
				onClickEdit,
				isEmbeddedContent,
				sendMetrics,
			],
		);

		const button = (
			<Tooltip
				tag="span"
				content={
					<FormattedMessage
						id="edit-button.tooltip"
						defaultMessage="Edit"
						description="tooltip for button to edit"
					/>
				}
				shortcut={editShortcutStrings}
			>
				<StyledButton
					appearance="subtle"
					iconBefore={
						<EditIcon
							label=""
							color={token('color.icon.subtle')}
							spacing={fg('confluence_frontend_content_wrapper') ? 'none' : 'spacious'}
						/>
					}
					ref={editButtonRef}
					onClick={enforceStorageLimit(handleButtonClick)}
					href={shouldBlockCreate ? undefined : routeType.toUrl(linkParams)}
					data-test-id={dataTestId}
					id="editPageLink"
					aria-label={intl.formatMessage(i18n.editLabel)}
				>
					{fg('confluence_frontend_content_wrapper') && (
						<Text
							color="color.text.subtle"
							align="center"
							weight="medium"
							data-vc="edit-button-text"
						>
							{intl.formatMessage(i18n.editText)}
						</Text>
					)}
				</StyledButton>
			</Tooltip>
		);

		if (isFabricEligibilityLoading) {
			return (
				<Spinner
					interactionName={
						fg('ufo-manual-experimental-holds-confluence')
							? 'edit-content-button-preloader'
							: undefined
					}
					size="small"
					testId="edit-content-button-preloader"
				/>
			);
		} else {
			return (
				<EditContentButtonPreloader
					key={contentId}
					contentId={contentId}
					spaceKey={spaceKey}
					isEmbeddedEditor={isEmbeddedContent}
					editSource={localEditSource}
					sendPrefetchAnalyticsEvent={sendPrefetchAnalyticsEvent}
					predictivelyPrefetch
				>
					<GeneralShortcutListener accelerator={EDIT_SHORTCUT} listener={clickEditPencilButton} />
					{onRender()}
					{showConvertPagePopup && convertOnEditExpConfig.get('isEnabled', false) ? (
						<Popup
							isOpen={isPopupVisible}
							onClose={() => setIsPopupVisible(false)}
							placement="bottom-start"
							content={() => {
								return (
									<EditPopupEligibleMigration
										onClickEdit={onClickEdit}
										contentId={contentId}
										href={shouldBlockCreate ? undefined : routeType.toUrl(linkParams)}
										// Note: contentHash is required arg for experimentalMigratePages, but is unused by the BE API handler; we simply pass "" to the API.
										contentHash=""
										contentType={contentType}
										spaceKey={spaceKey}
									/>
								);
							}}
							offset={[-34, 8]}
							trigger={({ ref }) => <Box ref={ref}>{button}</Box>}
							shouldRenderToParent={fg('should-render-to-parent-should-be-trueenterprise-e')}
						/>
					) : (
						<Box>{button}</Box>
					)}
					<ExperienceSuccess name={VIEW_PAGE_EDIT_BUTTON_EXPERIENCE} />
				</EditContentButtonPreloader>
			);
		}
	},
);

export const EditContentButtonComponent = injectIntl(EditContentButtonComponentInner);

EditContentButtonComponent.propTypes = {
	contentId: PropTypes.string.isRequired,
	onRender: PropTypes.func,
	isCompanyHubButton: PropTypes.bool,
	isEmbeddedContent: PropTypes.bool,
	isFabricSupported: PropTypes.bool,
};
